import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MailOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast } from '../../../../../../common/helpers/toast';
import styles from './EmailAnalytics.module.css';
import { AnalyticsFilterByDate } from '../BotAnalytics/AnalyticsFilterByTime/AnalyticsFilterByDate';
import {
  EmailFilterTabsType,
  EMAIL_FILTER_TABS,
} from '@mm/state/controllers/BotAnalyticsController';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../common/components/TableGrid/TableGrid';
import { ReactComponent as EmptyStats } from '../../../../../components/SlideoutMenu/menus/SlideoutEmailStats/empty-stats.svg';
import { PaginationNav } from '../../../../../../common/components/PaginationNav/PaginationNav';
import { selectEmailAnalytics } from '@mm/state/selectors/botAnalyticsSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { EmailSummaryStats } from './EmailSummaryStats';
import { AutomationRowWithEmailStat } from './AutomationRowWithEmailStat';

type Props = {
  header?: string;
  css?: {
    container?: string;
    header?: string;
    headerWitchChildren?: string;
    goToAutomationsButton?: string;
    box?: string;
    calendarTitle?: string;
  };
  asAutomationRows?: boolean;
  perPage?: number;
  withPagination?: boolean;
  onlySummaryStats?: boolean;
  calendarTitle?: string;
  headerRightChildren?: ReactNode;
};

const renderNumberCell =
  field =>
  ({ item }) =>
    <GridCell>{item[field] || 0}</GridCell>;

const renderPrecentageCell =
  field =>
  ({ item }) =>
    (
      <GridCell>
        {((item[field] * 100) / (item.sent !== 0 ? item.sent : 1)).toFixed(0) ||
          0}
        %
      </GridCell>
    );

const renderTextCell =
  field =>
  ({ item }) =>
    (
      <GridCell className={item?.campaignId ? styles.link : ''}>
        {item[field] || ''}
      </GridCell>
    );

export const calculateRate = (
  sent: number,
  currentOpenOrClickStat?: number | null,
) => {
  if (currentOpenOrClickStat && sent !== 0) {
    return `${((Number(currentOpenOrClickStat) * 100) / sent).toFixed(0)}%`;
  }
  return '0%';
};

export const EmailAnalytics = (props: Props) => {
  const {
    header,
    css,
    asAutomationRows,
    perPage,
    withPagination = true,
    onlySummaryStats,
    calendarTitle,
    headerRightChildren,
  } = props;
  const botId = useSelector(activeChatbotIdSelector);
  const busy = useSelector((state: StoreState) => state.botAnalytics.busy);
  const emailAnalytics = useSelector(selectEmailAnalytics);
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    moment().subtract(30, 'days'),
  );
  const [activeCampaign, setActiveCampaign] = useState<
    | {
        name: string;
        campaignId: number;
      }
    | undefined
  >(undefined);
  const [activeWidgetId, setActiveWidgetId] = useState(undefined);
  const [activeFilter, setActiveFilter] =
    useState<EmailFilterTabsType>('automations');
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment());
  const [dateRangeMode, setDateRangeMode] = useState<string | null>(
    'Last 30 days',
  );
  const { retrieveEmailAnalytics, clearEmailAnalytics } =
    useBotAnalyticsCRUDActions();
  const history = useHistory();
  const { updateCampaign } = useCampaignCRUDActions();

  const pagination = emailAnalytics[activeFilter]?.pagination;
  const summaryStats = emailAnalytics?.summary?.stats;
  const currentStat = summaryStats?.[0]?.current;
  const tabStats = emailAnalytics[activeFilter]?.stats;
  const hasAutomations = !!pagination?.total;

  const retrieveStats = async (
    tab: EmailFilterTabsType,
    campaignId?: number,
    widgetDataEmailId?: number,
    page?: number,
    perPage?: number,
  ) => {
    const res = await retrieveEmailAnalytics({
      botId,
      tab,
      campaignId,
      widgetDataEmailId,
      page,
      perPage,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
    });

    return res;
  };

  const getStats = async ({
    tab,
    campaignId,
    widgetDataEmailId,
    page,
    perPage,
  }: {
    tab: EmailFilterTabsType;
    campaignId?: number;
    widgetDataEmailId?: number;
    page?: number;
    perPage?: number;
  }) => {
    try {
      await retrieveStats(tab, campaignId, widgetDataEmailId, page, perPage);
    } catch (err) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const navigateToAutomation = (campaignId?: number) => {
    if (!campaignId) return;
    history.push(
      BotBuilderRoutes.automationEdit.getLink({
        chatbotId: botId,
        id: campaignId,
      }),
    );
  };

  const navigateToAutomationsHome = () => {
    history.push(BotBuilderRoutes.automationHome.getLink({ chatbotId: botId }));
  };

  const onStatusToggle = async params => {
    await updateCampaign({ ...params }, botId);
  };

  const onFilterTabClick = (
    filter: EmailFilterTabsType,
    campaignId?: number,
    widgetDataEmailId?: number,
  ) => {
    setActiveFilter(filter);
    getStats({
      tab: filter,
      campaignId,
      widgetDataEmailId,
      page: pagination?.page,
      perPage: perPage || pagination?.perPage,
    });
  };

  useEffect(() => {
    getStats({ tab: 'summary' });
    if (!onlySummaryStats) {
      getStats({
        tab: 'automations',
        page: pagination?.page,
        perPage: perPage || pagination?.perPage,
      });
    }

    return () => {
      clearEmailAnalytics();
    };
  }, [startDate, endDate]);

  const filterTabs = [
    {
      name: 'Automation',
      filter: EMAIL_FILTER_TABS[0] || EMAIL_FILTER_TABS[3],
    },
    // Uncomment when BE is ready

    // {
    //   name: 'Sender Email',
    //   filter: EMAIL_FILTER_TABS[1],
    // },
    // { name: 'Sender Domain', filter: EMAIL_FILTER_TABS[2] },
  ];

  const cells = [
    {
      label: 'Automation',
      keyName: 'automation',
      Cell: ({ item }) => (
        <GridCell
          className={styles.link}
          onClick={() => {
            onFilterTabClick('by_automation', item?.campaignId);
            setActiveCampaign({
              name: item.campaign,
              campaignId: item?.campaignId,
            });
          }}
        >
          <span className={styles.text}> {item.campaign} </span>

          <span className={styles.viewEmails}>
            View emails <ArrowRightOutlined />
          </span>
        </GridCell>
      ),
      show: activeFilter === 'automations',
    },
    {
      label: 'Automation’s Emails',
      keyName: 'widget',
      Cell: ({ item }) => (
        <GridCell
          className={styles.link}
          onClick={() => {
            onFilterTabClick('by_widget', undefined, item?.widgetId);
            setActiveWidgetId(item.widgetId);
          }}
        >
          <div className={styles.text}>
            <MailOutlined style={{ color: '#8C8C8C' }} />
            {item.widget}
          </div>

          <span className={styles.viewEmails}>
            View emails <ArrowRightOutlined />
          </span>
        </GridCell>
      ),
      show: activeFilter === 'by_automation',
    },
    {
      label: 'Email',
      keyName: 'email',
      Cell: renderTextCell('email'),
      show: activeFilter === 'by_widget',
    },
    {
      label: 'Sender Email',
      keyName: 'senderEmail',
      Cell: renderTextCell('senderEmail'),
      show: activeFilter === 'sender_emails',
    },
    {
      label: 'Sender Domain',
      keyName: 'senderDomain',
      Cell: renderTextCell('domain'),
      show: activeFilter === 'sender_domains',
    },
    {
      label: 'Sent Emails',
      keyName: 'sent',
      Cell: renderNumberCell('sent'),
      show: activeFilter !== 'by_widget',
    },
    {
      label: 'Opened',
      keyName: 'opened',
      Cell: renderNumberCell('open'),
      show: activeFilter === 'by_widget',
    },
    {
      label: 'Open Rate',
      keyName: 'open',
      Cell: renderPrecentageCell('open'),
      show: activeFilter !== 'by_widget',
    },
    {
      label: 'Clicked',
      keyName: 'clicked',
      Cell: renderNumberCell('click'),
      show: activeFilter === 'by_widget',
    },
    {
      label: 'Click Rate',
      keyName: 'click',
      Cell: renderPrecentageCell('click'),
      show: activeFilter !== 'by_widget',
    },
  ];

  const gridTemplateColumns = `55% 15% 15% 15%`;
  return (
    <div className={classNames(styles.container, css?.container)}>
      <div className={classNames(styles.header, css?.header)}>
        <div
          className={classNames(
            styles.headerWithChildren,
            css?.headerWitchChildren,
          )}
        >
          {header || 'Outreach Analytics'}
          {headerRightChildren}
        </div>
        <div
          className={classNames(styles.calendarContainer, css?.calendarTitle)}
        >
          {asAutomationRows
            ? ''
            : calendarTitle
            ? calendarTitle
            : 'View by date'}
          <AnalyticsFilterByDate
            className={styles.calendar}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
          />
        </div>
      </div>

      <EmailSummaryStats css={{ box: css?.box }} />

      {!onlySummaryStats && (
        <>
          {!asAutomationRows && (
            <div className={styles.filters}>
              Breakout by:
              {filterTabs.map(item => (
                <div
                  key={item.name}
                  className={classNames(styles.filterTab, {
                    [styles.activeFilter]:
                      activeFilter === item.filter ||
                      (item.filter === 'automations' &&
                        (activeFilter === 'by_automation' ||
                          activeFilter === 'by_widget')),
                  })}
                  onClick={() => onFilterTabClick(item.filter)}
                >
                  {item.name}
                </div>
              ))}
            </div>
          )}
          {(activeFilter === 'by_automation' ||
            activeFilter === 'by_widget') && (
            <>
              <div
                className={styles.backButton}
                onClick={() =>
                  activeFilter === 'by_automation'
                    ? onFilterTabClick('automations')
                    : onFilterTabClick(
                        'by_automation',
                        activeCampaign?.campaignId,
                      )
                }
              >
                <ArrowLeftOutlined />
                {activeFilter === 'by_automation'
                  ? 'Back to all Automations'
                  : `Back to Automation's emails`}
              </div>

              <div className={styles.stat}>{activeCampaign?.name}</div>
            </>
          )}

          {asAutomationRows &&
            tabStats?.map(entry => {
              const [data] = Object.entries(entry);
              const [_, info] = data || [];

              return (
                <AutomationRowWithEmailStat
                  key={info.campaignId}
                  name={info.campaign}
                  sent={info.sent || 0}
                  opened={
                    calculateRate(Number(currentStat?.sent), info?.open) || ''
                  }
                  clicked={
                    calculateRate(Number(currentStat?.sent), info?.click) || ''
                  }
                  onClick={() => navigateToAutomation(info?.campaignId)}
                  active={
                    info.active &&
                    (info.status === 'queued' || info.status === 'sending')
                  }
                  onToggle={active =>
                    onStatusToggle({
                      id: info.campaignId,
                      active,
                      status: active ? 'queued' : 'paused',
                    })
                  }
                />
              );
            })}

          {!asAutomationRows && (
            <div className={styles.grid}>
              <Grid className="integrations-grid">
                <GridLabels
                  className="integrations-grid__labels connections-panel__grid-labels"
                  gridTemplateColumns={gridTemplateColumns}
                >
                  {cells
                    .filter(cell => cell.show)
                    .map(cell => (
                      <TableLabel
                        key={cell.keyName || cell.label}
                        dataItem={cell}
                        orderByAtTheEnd
                      />
                    ))}
                </GridLabels>
                {!tabStats?.length && !busy && (
                  <div className={styles.empty}>
                    <h1>No Data</h1>
                    <span className={styles.emptyImage}>
                      <EmptyStats />
                    </span>
                  </div>
                )}
                {tabStats?.length
                  ? tabStats.map(entry => {
                      const [data] = Object.entries(entry);
                      const [_, info] = data || [];
                      const item = { ...info, id: Math.random() };
                      return (
                        <GridRow
                          className="integrations-grid__rows-container"
                          key={`${info.email}_${Math.random()}`}
                          gridTemplateColumns={gridTemplateColumns}
                        >
                          {cells
                            .filter(cell => cell.show)
                            .map(({ Cell, keyName }) => (
                              <Cell
                                key={`${keyName}_${Math.random()}`}
                                item={item}
                              />
                            ))}
                        </GridRow>
                      );
                    })
                  : null}
              </Grid>
            </div>
          )}
          {!!pagination?.page && withPagination && (
            <PaginationNav
              className={styles.pagination}
              pagination={pagination}
              onChangePage={page =>
                getStats({
                  tab: activeFilter,
                  campaignId: activeCampaign?.campaignId,
                  widgetDataEmailId: activeWidgetId,
                  page,
                  perPage: pagination.perPage,
                })
              }
              rangeLength={5}
              withEllipsis
            />
          )}
        </>
      )}
    </div>
  );
};
